import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { useRef, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { useAppDispatch, useAppSelector, useUser } from '../../app/hooks';
import { ModalPortal } from '../../components/modal-portal/ModalPortal';
import { getAssetTypeName, useOpenAsset } from '../../data/assets/helpers';
import { closeOpenedAsset, selectQueriedAssets, setInitialAssetsState } from '../../data/assets/queriedAssetsSlice';
import { selectTemplatesAndCombinedOntology, setIntialTemplatesState } from '../../data/templateSlice';
import styles from './NavigationBar.module.scss';
import { useApiContext } from '../../api';
import { removeDashboard, setInitialDashboardState } from '../visual-analytics/data/dashboardsSlice';
import { removeDataContext, setInitialDataContextState } from '../../data/analyticsDataSlice';
import useResizeObserver from '../visual-analytics/hooks/resize-observer';
import { changeTheme, currentTheme } from '../themes/themes';
import { TaskExecutionType } from '../../api/models/tasks';
import {
  selectTaskExecution,
  setInitialTaskExecutionsState,
  setUnreadTaskExecutions,
} from '../../data/taskExecutionSlice';
import { setInitialTaskDefinitionState } from '../../data/taskDefinitionSlice';
import { setIntialPollingState } from '../../data/pollingSlice';

export function NavigationBar() {
  const refButton = useRef<HTMLDivElement>(null);
  const refNavigationContent = useRef<HTMLDivElement>(null);
  const refFixedContent = useRef<HTMLDivElement>(null);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const openAsset = useOpenAsset();
  const api = useApiContext();
  const { user, setUser } = useUser();
  const [dropdownVisible, setDropdownVisible] = useState(false);
  const [showOrganizationModal, setShowOrganizationModal] = useState(false);
  const [itemsToVisualize, setItemsToVisualize] = useState(0);

  const [runningTasks, setRunningTasks] = useState<TaskExecutionType[]>([]);
  const taskExecutions = useAppSelector(selectTaskExecution);
  const [showUnreadTasks, setShowUnreadTasks] = useState(false);

  const { ontology } = useAppSelector(selectTemplatesAndCombinedOntology);
  const queriedAssetsData = useAppSelector(selectQueriedAssets);
  const location = useLocation();

  useEffect(() => {
    const updatedRunningTasks = taskExecutions.taskExecutionList.filter(
      (task) => task.status === 'running' || task.status === 'queued',
    );
    if (runningTasks.length > 0) {
      const tasksFinished = runningTasks.filter((task) => {
        return !updatedRunningTasks.map((t) => t.taskExecutionId).includes(task.taskExecutionId);
      });
      if (tasksFinished.length > 0) {
        setShowUnreadTasks(true);
        dispatch(setUnreadTaskExecutions({ taskExecutions: tasksFinished }));
      }
    }
    setRunningTasks(updatedRunningTasks);
  }, [taskExecutions.taskExecutionList]);

  useEffect(() => {
    if (taskExecutions.unreadTaskExecutionList.length === 0) {
      setShowUnreadTasks(false);
    }
  }, [taskExecutions.unreadTaskExecutionList]);

  useResizeObserver(refNavigationContent, () => {
    calculateItemsToVisualize();
  });

  function handleLogout() {
    api.logout(true);
    setDropdownVisible(false);
  }

  function tabStyle(routeId: string) {
    if (location.pathname == '/' + routeId) return styles.itemActive;
    return styles.item;
  }

  const fixedMenuItems = [
    <div key="start-page-item" className={tabStyle('')} onClick={() => navigate('')}>
      <div className={styles.itemIcon}>
        <FontAwesomeIcon icon="house" />
      </div>
      Start page
    </div>,
    <div
      key="task-executions-item"
      className={`${tabStyle('task-executions')} ${styles.taskExecutionsItem}`}
      onClick={() => navigate('/task-executions')}
    >
      <div className={styles.itemIcon}>
        <FontAwesomeIcon icon={['far', 'bars-progress']} />
      </div>
      <div>Task executions</div>
      {showUnreadTasks && <div className={styles.notificationDot}></div>}
    </div>,
  ];

  const openPrivacyStatement = () => {
    const PRIVACY_URL = import.meta.env.VITE_PRIVACY_URL;
    window.open(PRIVACY_URL, '_blank', 'noopener noreferrer');
  };

  const handleOrganizationSwitch = (organizationId: string) => {
    setDropdownVisible(false);
    setShowOrganizationModal(false);
    api.user
      .setOrganization(organizationId)
      .then((response) => {
        if (typeof response === 'string') {
          throw new Error(response);
        }
        const updatedUser = { ...user, ...response };
        return setUser(updatedUser);
      })
      .then(() => {
        navigate('');
      })
      .then(() => {
        return dispatch(setInitialAssetsState());
      })
      .then(() => {
        return dispatch(setIntialPollingState());
      })
      .then(() => {
        return Promise.all([
          dispatch(setInitialDataContextState()),
          dispatch(setInitialDashboardState()),
          dispatch(setIntialTemplatesState()),
          dispatch(setInitialTaskExecutionsState()),
          dispatch(setInitialTaskDefinitionState()),
        ]);
      })
      .then(() => {
        return api.logout(false);
      })
      .catch((error) => {
        throw new Error(error);
      });
  };

  const calculateItemsToVisualize = () => {
    const footerHeight = 50;
    const navBarHeight =
      refNavigationContent.current!.getBoundingClientRect().height -
      refFixedContent.current!.getBoundingClientRect().height -
      footerHeight;
    const item = document.querySelector(`.${styles.item}, .${styles.itemActive}`);
    const itemStyles = window.getComputedStyle(item!);
    const itemHeight =
      item!.getBoundingClientRect().height + parseFloat(itemStyles.marginTop) + parseFloat(itemStyles.fontSize); // add  the fontSize one extra time since navItems will always have double lines, but might not exist on Render
    setItemsToVisualize(Math.floor(navBarHeight! / itemHeight));
  };

  useEffect(() => {
    calculateItemsToVisualize();
  }, []);

  // @todo: add backup option is user is not authenticated => 403 page
  return (
    <>
      <div className={styles.topBar} id="appTopBar">
        <div ref={refButton} className={styles.topBarRightSection} onClick={() => setDropdownVisible(!dropdownVisible)}>
          {/* <div className={styles.username}>{user?.name}</div> */}
          <div className={styles.roundIcon}>
            <div>
              {user?.avatar ? (
                <img src={user.avatar} alt="user" width="43px" height="43px" />
              ) : (
                <FontAwesomeIcon icon="user" />
              )}
            </div>

            <div style={{ position: 'absolute', left: 0, top: 0, width: '100%', height: '100%', filter: 'blur(10px)' }}>
              <div
                style={{
                  position: 'absolute',
                  left: '10px',
                  top: '10px',
                  width: '20px',
                  height: '20px',
                  borderRadius: '50%',
                  backgroundColor: 'rgba(255,255,255,0.95)',
                }}
              />
            </div>
          </div>
          {dropdownVisible &&
            (showOrganizationModal ? (
              <ModalPortal
                referenceElement={refButton.current}
                offsetX={-180}
                disableBlur={true}
                close={() => {
                  setDropdownVisible(false);
                  setShowOrganizationModal(false);
                }}
              >
                <div className={styles.dropdownMenu}>
                  <button className={styles.logoutButton} onClick={() => setShowOrganizationModal(false)}>
                    <div className={styles.logoutButtonIcon}>
                      <FontAwesomeIcon icon={['fas', 'arrow-left']} />
                    </div>
                    <div className={styles.logoutButtonContent}>Return</div>
                  </button>
                  <hr />
                  {user.organizations &&
                    user.organizations
                      .sort((a, b) => a.organizationName.localeCompare(b.organizationName))
                      .map((organization) => {
                        return (
                          <button
                            key={`organization-${organization.organizationId}`}
                            className={styles.logoutButton}
                            onClick={() => handleOrganizationSwitch(organization.organizationId)}
                            disabled={organization.isActive}
                          >
                            <div className={styles.logoutButtonIcon}>
                              <FontAwesomeIcon
                                icon={organization.isActive ? ['fas', 'circle-check'] : ['fas', 'sitemap']}
                              />
                            </div>
                            <div className={styles.logoutButtonContent}>{organization.organizationName}</div>
                          </button>
                        );
                      })}
                </div>
              </ModalPortal>
            ) : (
              <ModalPortal
                referenceElement={refButton.current}
                offsetX={-180}
                disableBlur={true}
                close={() => setDropdownVisible(false)}
              >
                <div className={styles.dropdownMenu}>
                  <div className={styles.username}>{user?.name}</div>
                  <button
                    className={styles.logoutButton}
                    onClick={() => {
                      changeTheme();
                    }}
                  >
                    <div className={styles.logoutButtonIcon}>
                      <FontAwesomeIcon icon={['far', 'palette']} />
                    </div>
                    <div className={styles.logoutButtonContent}>Change theme</div>
                  </button>

                  {(user.canWrite || user.isOrganizationAdmin) && (
                    <>
                      <button
                        className={styles.logoutButton}
                        onClick={() => {
                          setDropdownVisible(false);
                          navigate('settings');
                        }}
                      >
                        <div className={styles.logoutButtonIcon}>
                          <FontAwesomeIcon icon={['far', 'gear']} />
                        </div>
                        <div className={styles.logoutButtonContent}>Settings</div>
                      </button>
                      <button
                        className={styles.logoutButton}
                        onClick={() => {
                          setDropdownVisible(false);
                          navigate('charts-documentation');
                        }}
                      >
                        <div className={styles.logoutButtonIcon}>
                          <FontAwesomeIcon icon={['fas', 'book']} />
                        </div>
                        <div className={styles.logoutButtonContent}>Charts documentation</div>
                      </button>
                    </>
                  )}

                  <button className={styles.logoutButton} onClick={openPrivacyStatement}>
                    <div className={styles.logoutButtonIcon}>
                      <FontAwesomeIcon icon={['far', 'file-shield']} />
                    </div>
                    <div className={styles.logoutButtonContent}>Privacy Policy</div>
                  </button>
                  {user.organizations && Object.keys(user.organizations).length > 1 && (
                    <button className={styles.logoutButton} onClick={() => setShowOrganizationModal(true)}>
                      <div className={styles.logoutButtonIcon}>
                        <FontAwesomeIcon icon={['far', 'repeat']} />
                      </div>
                      <div className={styles.logoutButtonContent}>Switch Organization</div>
                    </button>
                  )}
                  {(user.isOrganizationAdmin || user.isSystemAdmin) && (
                    <button
                      className={styles.logoutButton}
                      onClick={() => {
                        setDropdownVisible(false);
                        navigate('organization');
                      }}
                    >
                      <div className={styles.logoutButtonIcon}>
                        <FontAwesomeIcon icon={['fas', 'toolbox']} />
                      </div>
                      <div className={styles.logoutButtonContent}>Organization settings</div>
                    </button>
                  )}
                  {user.isSystemAdmin && (
                    <button
                      className={styles.logoutButton}
                      onClick={() => {
                        setDropdownVisible(false);
                        navigate('admin');
                      }}
                    >
                      <div className={styles.logoutButtonIcon}>
                        <FontAwesomeIcon icon={['fas', 'user-shield']} />
                      </div>
                      <div className={styles.logoutButtonContent}>Admin Portal</div>
                    </button>
                  )}
                  <button className={styles.logoutButton} onClick={handleLogout}>
                    <div className={styles.logoutButtonIcon}>
                      <FontAwesomeIcon icon={['far', 'right-from-bracket']} />
                    </div>
                    <div className={styles.logoutButtonContent}>Log out</div>
                  </button>
                </div>
              </ModalPortal>
            ))}
        </div>
      </div>
      <div className={styles.wrapper}>
        <div className={styles.blurrer}>
          <div
            className={styles.disc}
            style={{
              top: '0',
              left: '150px',
              width: '400px',
              height: '40%',
              backgroundColor: currentTheme.colors.supportSec.opacity(0.5).toCanvasString(),
            }}
          />
          <div
            className={styles.disc}
            style={{
              top: '20%',
              left: '-20px',
              width: '120px',
              height: '15%',
              backgroundColor: currentTheme.colors.text.opacity(0.7).toCanvasString(),
            }}
          />
          <div
            className={styles.disc}
            style={{
              top: '40%',
              left: '-20px',
              width: '170px',
              height: '25%',
              backgroundColor: currentTheme.colors.supportSec.lighten(0.5).opacity(0.5).toCanvasString(),
            }}
          />
          <div
            className={styles.disc}
            style={{
              top: '70%',
              left: '40px',
              width: '300px',
              height: '30%',
              backgroundColor: currentTheme.colors.supportPrim.opacity(0.3).toCanvasString(),
            }}
          />
        </div>
        <div className={styles.navigationContent} ref={refNavigationContent}>
          <div ref={refFixedContent}>
            <div style={{ padding: 0 }}>
              <img src="/bioverse_logo_allwhite.png" style={{ width: '200px' }} id="bioverse_logo" />
            </div>
            {fixedMenuItems.map((item) => item)}

            <div className={styles.separator} />
          </div>

          {queriedAssetsData.openedAssets.slice(0, itemsToVisualize).map((assetInfo) => (
            <div
              className={tabStyle(`asset/${assetInfo.id}`)}
              onClick={() => openAsset(assetInfo.id)}
              key={assetInfo.id}
            >
              {getAssetTypeName(ontology.assetTypes[assetInfo.type])}
              <br />
              {assetInfo.displayName}
              <div
                className={styles.itemCloseButton}
                onClick={(ev) => {
                  ev.stopPropagation();
                  if (location.pathname == `/asset/${assetInfo.id}`) {
                    navigate(-1);
                  }
                  setTimeout(() => {
                    dispatch(closeOpenedAsset({ assetId: assetInfo.id }));
                    dispatch(removeDataContext(assetInfo.id));
                    dispatch(removeDashboard(assetInfo.id));
                  }, 25);
                }}
              >
                <FontAwesomeIcon icon="xmark" />
              </div>
            </div>
          ))}
          <div className={styles.navigationFooter}>
            <a href="https://lizard.bio" target="_blank" rel="noreferrer">
              <img src="/bioliz_white_logo1.png" style={{ width: '90px' }} id="biolizard_logo" />
            </a>
          </div>
        </div>
      </div>
    </>
  );
}
